/**
 * F60 — CSV export of the current search. Pages through
 * GET /admin/ops/logs/export (1,000 rows a page, signed continuation token,
 * 10,000-row cap), then saves one file. The range is frozen when the modal
 * opens, so a rolling preset doesn't shift between pages.
 */
import { useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { OPS_LOGS_EXPORT_MAX_ROWS, type OpsLogsFilters } from '@bravemove/types';
import { Banner, Button, Modal, ProgressBar } from '@bravemove/ui-web';
import { adminOpsLogsApi, type OpsRange } from '../../../api/opsLogs';
import { downloadText, errorKey } from '../opsHelpers';

export interface ExportLogsModalProps {
  readonly open: boolean;
  readonly range: OpsRange;
  readonly filters: OpsLogsFilters;
  readonly onClose: () => void;
}

const MAX_PAGES = 12;

type Phase =
  | { kind: 'idle' }
  | { kind: 'running'; rows: number }
  | { kind: 'done'; rows: number; truncated: boolean }
  | { kind: 'failed'; errorKey: string };

export function ExportLogsModal({ open, range, filters, onClose }: ExportLogsModalProps) {
  const { t } = useI18n();
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });

  const close = () => {
    if (phase.kind === 'running') return;
    setPhase({ kind: 'idle' });
    onClose();
  };

  const run = async () => {
    const chunks: string[] = [];
    let rows = 0;
    let truncated = false;
    let cursor: string | undefined;
    setPhase({ kind: 'running', rows: 0 });
    try {
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const result = await adminOpsLogsApi.exportPage({
          ...range,
          ...filters,
          ...(cursor ? { cursor } : {}),
        });
        chunks.push(result.csv);
        rows += result.rows;
        truncated = result.truncated;
        setPhase({ kind: 'running', rows });
        // A repeated cursor would loop forever — stop instead.
        if (!result.nextCursor || result.nextCursor === cursor) break;
        cursor = result.nextCursor;
      }
      const stamp = (iso: string) => iso.slice(0, 16).replace(/[:T]/gu, '-');
      downloadText(
        `bravemove-api-logs_${stamp(range.from)}_${stamp(range.to)}.csv`,
        chunks,
        'text/csv',
      );
      setPhase({ kind: 'done', rows, truncated });
    } catch (err) {
      setPhase({ kind: 'failed', errorKey: errorKey(err) });
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={t('admin:opsLogs.export.title')}
      description={t('admin:opsLogs.export.description', { max: OPS_LOGS_EXPORT_MAX_ROWS })}
      dismissOnBackdrop={phase.kind !== 'running'}
      closeLabel={t('admin:opsLogs.detail.close')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={close} disabled={phase.kind === 'running'}>
            {phase.kind === 'done'
              ? t('admin:opsLogs.export.close')
              : t('admin:opsLogs.export.cancel')}
          </Button>
          {phase.kind !== 'done' ? (
            <Button
              variant="primary"
              size="sm"
              icon="download"
              loading={phase.kind === 'running'}
              onClick={() => void run()}
              data-testid="ops-export-start"
            >
              {t('admin:opsLogs.export.start')}
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="space-y-3" data-testid="ops-export-modal">
        {phase.kind === 'running' ? (
          <div className="space-y-2">
            <ProgressBar
              value={phase.rows}
              max={OPS_LOGS_EXPORT_MAX_ROWS}
              label={t('admin:opsLogs.export.progressLabel')}
            />
            <p className="text-small text-ink-2">
              {t('admin:opsLogs.export.progress', { rows: phase.rows })}
            </p>
          </div>
        ) : null}
        {phase.kind === 'done' ? (
          <Banner tone={phase.truncated ? 'warning' : 'success'}>
            {phase.truncated
              ? t('admin:opsLogs.export.truncated', { rows: phase.rows })
              : t('admin:opsLogs.export.done', { rows: phase.rows })}
          </Banner>
        ) : null}
        {phase.kind === 'failed' ? (
          <Banner tone="danger">{t(`admin:opsLogs.errors.${phase.errorKey}`)}</Banner>
        ) : null}
      </div>
    </Modal>
  );
}
